import { ScanText, Eye, Trash2 } from "lucide-react";
import { VARIANT_LABELS } from "./constants";

export function TextsTab({
  ocrFiles, setOcrFiles, ocrLoading, ocrProgress, ocrResult, setOcrResult,
  pubVariant, setPubVariant, pubCategoryId, setPubCategoryId, publishing,
  editorZoom, setEditorZoom, ocrTexts, categories,
  onExtract, onUpdateText, onViewText, onDeleteText, onPublish,
}) {
  return (
    <div data-testid="admin-ocr-panel" className="grid grid-cols-1 gap-8 lg:grid-cols-2">
      <div className="contents">
        <form onSubmit={onExtract} data-testid="ocr-upload-form" className="space-y-5 lg:order-1">
          <label
            data-testid="ocr-file-drop"
            className="flex cursor-pointer flex-col items-center gap-3 border border-dashed border-white/20 px-6 py-12 text-center transition-colors duration-300 hover:border-[#FFD700]/50"
          >
            <ScanText className="h-6 w-6 text-[#666666]" />
            <span className="text-sm text-[#A1A1A6]">
              {ocrFiles.length
                ? `${ocrFiles.length} image${ocrFiles.length > 1 ? "s" : ""} selected`
                : "Drop chart images — select many at once (jpg, png, webp)"}
            </span>
            <input
              data-testid="ocr-file-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => setOcrFiles(Array.from(e.target.files || []))}
            />
          </label>
          {ocrFiles.length > 0 && (
            <div data-testid="ocr-file-list" className="max-h-28 space-y-1 overflow-y-auto border border-white/10 p-3">
              {ocrFiles.map((f, i) => (
                <p key={i} className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-[#666666]">
                  {i + 1}. {f.name}
                </p>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <select
              data-testid="ocr-bulk-variant"
              value={pubVariant}
              onChange={(e) => setPubVariant(e.target.value)}
              className="border border-white/10 bg-black px-4 py-3 text-xs text-white outline-none focus:border-[#FFD700]/60"
            >
              {Object.entries(VARIANT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              data-testid="ocr-bulk-category"
              value={pubCategoryId}
              onChange={(e) => setPubCategoryId(e.target.value)}
              className="border border-white/10 bg-black px-4 py-3 text-xs text-white outline-none focus:border-[#FFD700]/60"
            >
              <option value="auto">Auto-detect from heading</option>
              <option value="">Text only — no publish</option>
              {(categories[pubVariant] || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <button
            data-testid="ocr-extract-btn"
            type="submit"
            disabled={ocrLoading}
            className="rounded-full bg-[#FFD700] px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {ocrLoading
              ? `Converting ${ocrProgress}...`
              : ocrFiles.length > 1
                ? `Convert & publish all (${ocrFiles.length})`
                : "Convert & publish"}
          </button>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666]">
            Text மட்டும் வேணும்னா category-ஐ காலி விடு
          </p>
        </form>

        {ocrResult && (
          <div className="mt-8 space-y-4 lg:order-3 lg:col-span-2" data-testid="ocr-result-panel">
            <input
              data-testid="ocr-name-input"
              value={ocrResult.name}
              onChange={(e) => setOcrResult({ ...ocrResult, name: e.target.value })}
              className="w-full border border-white/10 bg-black px-4 py-3 text-sm font-semibold text-white outline-none focus:border-[#FFD700]/60"
            />
            {ocrResult.detected && (
              <p data-testid="ocr-detected-hint" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676]">
                Auto-detected: {ocrResult.detected.category_name}
              </p>
            )}
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666]">Zoom</span>
              {[
                ["text-xs", "S"],
                ["text-sm", "M"],
                ["text-base", "L"],
              ].map(([cls, label]) => (
                <button
                  key={cls}
                  data-testid={`ocr-zoom-${label.toLowerCase()}`}
                  onClick={() => setEditorZoom(cls)}
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-bold transition-colors duration-300 ${
                    editorZoom === cls
                      ? "border-[#FFD700] bg-[#FFD700] text-black"
                      : "border-white/15 text-[#A1A1A6] hover:border-white/40"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <textarea
              data-testid="ocr-result-text"
              value={ocrResult.text}
              onChange={(e) => setOcrResult({ ...ocrResult, text: e.target.value })}
              rows={18}
              wrap="off"
              className={`w-full overflow-x-auto whitespace-pre border border-white/10 bg-black px-4 py-3 font-mono ${editorZoom} leading-relaxed text-[#FDFBF7] outline-none focus:border-[#FFD700]/60`}
            />
            <button
              data-testid="ocr-save-btn"
              onClick={onUpdateText}
              className="rounded-full border border-[#FFD700]/60 px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#FFD700] transition-colors duration-300 hover:bg-[#FFD700] hover:text-black"
            >
              Save changes
            </button>

            <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-5">
              <select
                data-testid="ocr-publish-variant"
                value={pubVariant}
                onChange={(e) => setPubVariant(e.target.value)}
                className="border border-white/10 bg-black px-4 py-3 text-xs text-white outline-none focus:border-[#FFD700]/60"
              >
                {Object.entries(VARIANT_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                data-testid="ocr-publish-category"
                value={pubCategoryId}
                onChange={(e) => setPubCategoryId(e.target.value)}
                className="border border-white/10 bg-black px-4 py-3 text-xs text-white outline-none focus:border-[#FFD700]/60"
              >
                <option value="auto">Auto-detect</option>
                {(categories[pubVariant] || []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                data-testid="ocr-publish-btn"
                onClick={onPublish}
                disabled={publishing}
                className="rounded-full bg-[#FFD700] px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
              >
                {publishing ? "Publishing..." : "Render & publish as chart"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div data-testid="ocr-saved-list" className="space-y-2 lg:order-2">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
          Saved extractions ({ocrTexts.length})
        </p>
        {ocrTexts.length === 0 && (
          <p className="border border-dashed border-white/15 py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-[#666666]">
            No extractions yet
          </p>
        )}
        {ocrTexts.map((t) => (
          <div key={t.id} data-testid={`ocr-item-${t.id}`} className="border border-white/10 bg-black/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="truncate text-sm font-semibold">{t.name}</p>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`ocr-view-${t.id}`}
                  onClick={() => onViewText(t.id)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-[#A1A1A6] transition-colors duration-300 hover:border-[#FFD700]/60 hover:text-[#FFD700]"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`ocr-delete-${t.id}`}
                  onClick={() => onDeleteText(t.id)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-[#A1A1A6] transition-colors duration-300 hover:border-[#E63946]/60 hover:text-[#E63946]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <p className="mt-2 line-clamp-2 whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-[#666666]">
              {t.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
